import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, Clock, Heart, Users, Shield, Award, BookOpen, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export const DonorsPage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  const location = useLocation();
  useEffect(() => {
    const target = (location.state as any)?.scrollTo;
    if (target) {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.state]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bloodType: '',
    preferredDate: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const eligibilityRequirements = [
    {
      title: 'Basic Requirements',
      requirements: [
        'Age 17 years or older (16 with parental consent if allowed by state)',
        'Weight minimum 110 lbs',
        'In good general health and feeling well',
        'Temperature, pulse, and blood pressure within normal limits',
        'Hemoglobin levels: Female ≥ 12.5 g/dL, Male ≥ 13.0 g/dL'
      ]
    },
    {
      title: 'Health Considerations',
      requirements: [
        'No active infections or illness',
        'No fever in the past week',
        'Not pregnant or breastfeeding',
        'Completed antibiotic course if recently treated',
        'No recent tattoos or piercings (if equipment questionable)'
      ]
    }
  ];

  const donationTypes = [
    {
      title: 'Whole Blood',
      duration: '8-10 minutes',
      interval: '8 weeks',
      description: 'The most common type of donation. One unit of whole blood can help up to 3 patients.',
      image: `${BASE_URL}images/donation_process_1_4.jpg`
    },
    {
      title: 'Power Red',
      duration: '~45 minutes',
      interval: '16 weeks',
      description: 'Automated donation of concentrated red blood cells. Returns plasma and saline.',
      image: `${BASE_URL}images/donation_process_2_2.jpg`
    },
    {
      title: 'Platelets',
      duration: '~2 hours',
      interval: '7 days',
      description: 'Automated donation of platelets and some plasma. Important for cancer and surgery patients.',
      image: `${BASE_URL}images/donation_process_2_6.jpg`
    },
    {
      title: 'Plasma',
      duration: '~45 minutes',
      interval: 'Center-specific',
      description: 'Automated donation of plasma. Returns red blood cells and platelets to donor.',
      image: `${BASE_URL}images/medical_theme_2_6.png`
    }
  ];

  const donationProcess = [
    {
      step: 1,
      title: 'Registration',
      description: 'Sign in with your donor ID or provide identification',
      image: `${BASE_URL}images/donation_process_1_2.jpg`
    },
    {
      step: 2,
      title: 'Health History',
      description: 'Complete confidential questionnaire about your health and travel',
      image: `${BASE_URL}images/medical_theme_1_3.jpeg`
    },
    {
      step: 3,
      title: 'Mini-Physical',
      description: 'Quick health check including temperature, pulse, and hemoglobin',
      image: `${BASE_URL}images/medical_theme_2_1.jpg`
    },
    {
      step: 4,
      title: 'Donation',
      description: 'Comfortable donation process with professional phlebotomy team',
      image: `${BASE_URL}images/donation_process_1_4.jpg`
    },
    {
      step: 5,
      title: 'Refreshments',
      description: 'Rest and enjoy refreshments for 15 minutes before leaving',
      image: `${BASE_URL}images/benefits_icons_2_0.jpg`
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Health Benefits',
      description: 'May help improve cardiovascular health and reduce disease risk'
    },
    {
      icon: Award,
      title: 'Personal Satisfaction',
      description: 'Experience the fulfillment of knowing you\'ve saved lives'
    },
    {
      icon: Users,
      title: 'Community Impact',
      description: 'Your donation directly helps patients in your local community'
    },
    {
      icon: Shield,
      title: 'Free Health Screening',
      description: 'Receive basic health screening and blood type identification'
    },
    {
      icon: BookOpen,
      title: 'Educational Value',
      description: 'Learn about your health and blood donation process'
    },
    {
      icon: CheckCircle,
      title: 'Regular Check-ups',
      description: 'Ongoing monitoring of your health through regular donations'
    }
  ];

  const preparationTips = [
    {
      title: 'Before Donation',
      tips: [
        'Get a good night\'s sleep (7-8 hours)',
        'Eat iron-rich foods in the weeks leading up to donation',
        'Stay hydrated - drink plenty of water',
        'Eat a substantial meal 3 hours before donating',
        'Avoid fatty foods before donation',
        'Bring a valid photo ID',
        'Wear clothing with sleeves that can be rolled up'
      ],
      icon: '✅'
    },
    {
      title: 'After Donation',
      tips: [
        'Rest in the refreshment area for 15 minutes',
        'Drink plenty of fluids for the next 24-48 hours',
        'Avoid heavy lifting or vigorous exercise for 24 hours',
        'Keep your bandage on for several hours',
        'Eat iron-rich foods to help replenish your iron stores',
        'If you feel lightheaded, sit or lie down until you feel better',
        'Contact us if you develop a fever or remember important health info'
      ],
      icon: '💚'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">Become a Blood Donor</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            Learn about the donation process, eligibility requirements, and benefits of blood donation. Every donation can save up to 3 lives.
          </p>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Eligibility Requirements</h2>
            <p className="text-body-lg text-neutral-600">
              Basic requirements to ensure donor safety and blood quality
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {eligibilityRequirements.map((section, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.requirements.map((requirement, reqIndex) => (
                      <li key={reqIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-semantic-success mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-body text-neutral-700">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <div className="bg-primary-50 border-l-4 border-primary-600 p-6 rounded-lg max-w-4xl mx-auto">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="text-body font-semibold text-primary-900 mb-2">Important Note</h3>
                  <p className="text-body text-primary-700">
                    These requirements are general guidelines. Specific eligibility can vary by center and individual circumstances. 
                    Please consult with our staff to determine your eligibility for blood donation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Types */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Donation Types</h2>
            <p className="text-body-lg text-neutral-600">
              Different types of donations to meet various patient needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {donationTypes.map((type, index) => (
              <Card key={index} hover className="overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={type.image} 
                    alt={type.title} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <CardTitle>{type.title}</CardTitle>
                    <div className="flex items-center text-primary-600">
                      <Clock className="h-4 w-4 mr-1" />
                      <span className="text-small font-medium">{type.duration}</span>
                    </div>
                  </div>
                  <p className="text-body text-neutral-600 mb-4">{type.description}</p>
                  <div className="flex items-center text-semantic-success">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-small font-medium">Wait {type.interval} before next donation</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Donation Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">The Donation Process</h2>
            <p className="text-body-lg text-neutral-600">
              Step-by-step guide to your blood donation experience
            </p>
          </div>
          <div className="space-y-6">
            {donationProcess.map((process, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}>
                <div className="lg:w-1/2">
                  <Card className="h-full">
                    <CardContent>
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mr-4">
                          {process.step}
                        </div>
                        <h3 className="text-subtitle font-semibold text-neutral-900">{process.title}</h3>
                      </div>
                      <p className="text-body text-neutral-600">{process.description}</p>
                    </CardContent>
                  </Card>
                </div>
                <div className="lg:w-1/2 h-full flex items-center">
                  <div className="relative w-full h-56 sm:h-60 lg:h-64 overflow-hidden rounded-lg shadow-md">
                    <img 
                      src={process.image} 
                      alt={process.title} 
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-6">
                      <div className="text-white">
                        <span className="text-4xl font-bold text-white/80">0{process.step}</span>
                        <h3 className="text-xl font-semibold text-white">{process.title}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits of Donating */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Benefits of Donating</h2>
            <p className="text-body-lg text-neutral-600">
              Discover the positive impact of blood donation on both donors and recipients
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} hover className="text-center">
                  <CardContent>
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-primary-600" />
                    </div>
                    <CardTitle>{benefit.title}</CardTitle>
                    <p className="text-body text-neutral-600 mt-4">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mb-6">
              <img 
                src={`${BASE_URL}images/benefits_icons_1_0.jpg`} 
                alt="Health benefit 1" 
                className="w-full h-32 rounded-lg object-cover"
              />
              <img 
                src={`${BASE_URL}images/benefits_icons_1_3.jpeg`} 
                alt="Health benefit 2" 
                className="w-full h-32 rounded-lg object-cover"
              />
              <img 
                src={`${BASE_URL}images/benefits_icons_1_7.jpg`} 
                alt="Health benefit 3" 
                className="w-full h-32 rounded-lg object-cover"
              />
              <img 
                src={`${BASE_URL}images/benefits_icons_2_0.jpg`} 
                alt="Health benefit 4" 
                className="w-full h-32 rounded-lg object-cover"
              />
            </div>
            <p className="text-body text-neutral-600 max-w-2xl mx-auto">
              Regular blood donation may provide cardiovascular benefits and help maintain healthy iron levels. 
              Always consult with healthcare providers for personalized medical advice.
            </p>
          </div>
        </div>
      </section>

      {/* Preparation Tips */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Preparation Tips</h2>
            <p className="text-body-lg text-neutral-600">
              How to prepare before and after your blood donation
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {preparationTips.map((section, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{section.icon}</span>
                    <CardTitle>{section.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-semantic-success mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-body text-neutral-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-24 bg-primary-50" id="register-to-donate">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Register to Donate</h2>
            <p className="text-body-lg text-neutral-600">
              Take the first step towards saving lives. Fill out this form to register as a blood donor.
            </p>
          </div>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-small font-medium text-neutral-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-small font-medium text-neutral-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-small font-medium text-neutral-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-small font-medium text-neutral-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="bloodType" className="block text-small font-medium text-neutral-700 mb-2">
                      Blood Type (if known)
                    </label>
                    <select
                      id="bloodType"
                      name="bloodType"
                      value={formData.bloodType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select blood type</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="preferredDate" className="block text-small font-medium text-neutral-700 mb-2">
                      Preferred Donation Date
                    </label>
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <Button type="submit" size="lg">
                    <Heart className="mr-2 h-5 w-5" />
                    Register as Donor
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-title font-bold mb-6">Ready to Make a Difference?</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Join thousands of donors who are making a life-saving impact in our community. 
            Your next donation could be the one that saves a life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                <Heart className="mr-2 h-5 w-5" />
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
